import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  keepPreviousData,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import sortBy from "lodash-es/sortBy";
import { DateTime } from "luxon";
import React, { useId } from "react";
import { Outlet, useSearchParams } from "react-router-dom";

import { Failed, NoData } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import {
  itemsPerPage,
  Pagination,
} from "@/common/components/pagination/pagination";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { countryCodeToFlag } from "@/common/utils/unicode";
import { CompanyToPlanSource, GetCompaniesPlan } from "@/generated/api/users";
import { AutocompleteString } from "@/types";

import { Filter, SelectFilter } from "../components/table/filter/filter";
import { Table } from "../components/table/table";
import {
  authorizedCountriesQuery,
  companiesQuery,
  Company,
  useExportCompaniesMutation,
} from "./companies.api";

type TableMeta = {
  label: () => string;
  filterKey?:
    | "country"
    | "search"
    | "plan"
    | "plan-source"
    | "plan-valid"
    | "custom-prices";
  filterVariant?: "text" | "select";
};

const path = "companies";
const columnHelper = createColumnHelper<Company>();

const useCompanyParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const planValid = searchParams.get("plan-valid");
  const customPrices = searchParams.get("custom-prices");

  const params = {
    country: searchParams.get("country") ?? "",
    customPrices:
      customPrices === "true"
        ? true
        : customPrices === "false"
          ? false
          : undefined,
    page: parseInt(searchParams.get("page") ?? "1", 10),
    plan: (searchParams.get("plan") ?? "") as GetCompaniesPlan,
    planSource: (searchParams.get("plan-source") ?? "") as CompanyToPlanSource,
    planValid:
      planValid === "true" ? true : planValid === "false" ? false : undefined,
    search: searchParams.get("search") ?? "",
  };

  const handleParamChange = (
    key: AutocompleteString<
      "search" | "plan" | "plan-source" | "country" | "page" | "custom-prices"
    >,
    value: string,
  ) => {
    setSearchParams((c) => {
      c.set(key, value);
      return c;
    });
  };

  return { handleParamChange, params };
};

const columns = [
  columnHelper.accessor("id", {
    cell: () => null,
    header: () => null,
  }),
  columnHelper.accessor("country", {
    cell: (info) => countryCodeToFlag(info.getValue()),
    header: () => t`Země`,
    meta: {
      filterKey: "country",
      filterVariant: "select",
      label: () => t`Země`,
    } as TableMeta,
  }),
  columnHelper.accessor((row) => row.name + " / " + row.number, {
    cell: (info) => {
      const id = info.row.getValue<string>("id");

      return (
        <SearchParamsLink className="text-can-forest-teal" to={"./" + id}>
          {info.getValue()}
        </SearchParamsLink>
      );
    },
    header: () => t`Název / IČO`,
    id: "number",
    meta: {
      filterKey: "search",
      filterVariant: "text",
      label: () => t`Společnost`,
    },
  }),
  columnHelper.accessor("plan.type", {
    cell: (info) => info.getValue(),
    header: () => t`Forma předplatného`,
    id: "planType",
    meta: {
      filterKey: "plan",
      filterVariant: "select",
      label: () => t`Forma předplatného`,
    } as TableMeta,
  }),
  columnHelper.accessor("plan.source", {
    cell: (info) => info.getValue(),
    header: () => t`Zdroj předplatného`,
    id: "planSource",
    meta: {
      filterKey: "plan-source",
      filterVariant: "select",
      label: () => t`Zdroj předplatného`,
    } as TableMeta,
  }),
  columnHelper.accessor("plan.expiresAt", {
    cell: (info) => info.getValue()?.toLocaleString(DateTime.DATE_SHORT),
    header: () => t`Předplatné do`,
    id: "planExpiresAt",
    meta: {
      filterKey: "plan-valid",
      filterVariant: "select",
      label: () => t`Předplatné do`,
    } as TableMeta,
  }),
  columnHelper.accessor("hasCustomPrices", {
    cell: (info) => (info.getValue() ? "\u2713" : null),
    header: () => t`Vlastní ceny`,
    id: "customPrices",
    meta: {
      filterKey: "custom-prices",
      filterVariant: "select",
      label: () => t`Vlastní ceny`,
    } as TableMeta,
  }),
];

const Companies = () => {
  const titleKey = useId();
  const authorizedCountries = useSuspenseQuery(authorizedCountriesQuery());
  const { params, handleParamChange } = useCompanyParams();
  const companies = useQuery({
    placeholderData: keepPreviousData,
    ...companiesQuery(params),
  });
  const exportCompaniesMutation = useExportCompaniesMutation();

  // tanstack query `select` doesn't work with tanstack table
  const data = React.useMemo(
    () => ({
      companies: sortBy(
        companies.data?.data.map(
          (company) =>
            ({
              country: company.country,
              hasCustomPrices: company.hasCustomPrices ?? false,
              id: company.id,
              name: company.name,
              number: company.companyNumber,
              plan: {
                expiresAt:
                  company.subscriptions
                    .filter((s) => s.expiresAt)
                    .map((s) => DateTime.fromISO(s.expiresAt!))
                    .toSorted()
                    .at(0) ?? null,
                source: company.planSource,
                type: company.plan.type,
              },
            }) satisfies Company,
        ),
        (company) => company.name.toLowerCase(),
      ),
      totalSize: companies.data?.totalSize,
    }),
    [companies.data],
  );

  const table = useReactTable({
    columns,
    data: data.companies,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    state: {
      columnVisibility: {
        id: false,
        name: false,
      },
    },
  });

  const handleExport = () => {
    exportCompaniesMutation.mutate({
      ...params,
      // @ts-expect-error API is a bit off here
      planValid: params.planValid ?? "",
    });
  };

  if (companies.status === "pending" && companies.isLoading) {
    return (
      <>
        <Metadata key={titleKey} title={t`Správa společností`} />
        <Spinner className="mt-4" />
      </>
    );
  }

  if (companies.status === "error") {
    return <Failed error={companies.error} />;
  }

  return (
    <>
      <Metadata key={titleKey} title={t`Správa společností`} />
      <button
        className="flex items-center gap-2 my-2 mx-4 pt-2 text-can-forest-teal"
        disabled={exportCompaniesMutation.status === "pending"}
        onClick={handleExport}
      >
        <Trans>Exportovat</Trans>
        {exportCompaniesMutation.status === "pending" ? (
          <Spinner className="h-3 w-3" withHint={false} />
        ) : null}
      </button>
      <Table>
        <Table.Head
          filters={{
            country: (
              <SelectFilter
                column={table.getColumn("country")!}
                onFilterChange={handleParamChange}
                value={params.country}
                values={authorizedCountries.data.map((country) => ({
                  label: countryCodeToFlag(country.id),
                  value: country.id,
                }))}
              />
            ),
            customPrices: (
              <SelectFilter
                column={table.getColumn("customPrices")!}
                onFilterChange={handleParamChange}
                value={params.customPrices?.toString() ?? ""}
                values={[
                  { label: t`Ano`, value: "true" },
                  { label: t`Ne`, value: "false" },
                ]}
              />
            ),
            number: (
              <Filter
                column={table.getColumn("number")!}
                onFilterChange={handleParamChange}
                value={params.search}
              />
            ),
            planExpiresAt: (
              <SelectFilter
                column={table.getColumn("planExpiresAt")!}
                onFilterChange={handleParamChange}
                value={params.planValid?.toString() ?? ""}
                values={[
                  { label: t`Zaplacené`, value: "true" },
                  { label: t`Expirované`, value: "false" },
                ]}
              />
            ),
            planSource: (
              <SelectFilter
                column={table.getColumn("planSource")!}
                onFilterChange={handleParamChange}
                value={params.planSource}
                values={Object.values(CompanyToPlanSource).map((value) => ({
                  label: value,
                  value,
                }))}
              />
            ),
            planType: (
              <SelectFilter
                column={table.getColumn("planType")!}
                onFilterChange={handleParamChange}
                value={params.plan}
                values={[
                  // eslint-disable-next-line lingui/no-unlocalized-strings
                  { label: "Basic", value: "BASIC" },
                  // eslint-disable-next-line lingui/no-unlocalized-strings
                  { label: "Plus", value: "PLUS" },
                  // eslint-disable-next-line lingui/no-unlocalized-strings
                  { label: "Premium", value: "PREMIUM" },
                  // eslint-disable-next-line lingui/no-unlocalized-strings
                  { label: "Trader", value: "TRADER" },
                ]}
              />
            ),
          }}
          table={table}
        />
        {companies.status === "success" && companies.data.data.length > 0 ? (
          <Table.Body table={table} />
        ) : null}
      </Table>
      {companies.data ? (
        <>
          <Spacer className="h-5" />
          <Pagination
            getter={() => params.page}
            pageSize={itemsPerPage.oneHundred}
            setter={(page) => handleParamChange("page", page.toString())}
            total={companies.data.totalSize}
          />
        </>
      ) : null}
      {companies.status === "success" && companies.data.data.length === 0 ? (
        <div className="mt-4">
          <NoData>
            <Trans>Nebyly nalezeny žádné záznamy</Trans>
          </NoData>
        </div>
      ) : null}
      {companies.fetchStatus === "fetching" && !companies.isLoading ? (
        <Spinner withHint={false} />
      ) : null}
      <Outlet />
    </>
  );
};

export { Companies, path };
